import React from 'react';
import styled, { css } from 'styled-components';
import { colors, space } from '@ardoq/design-tokens';
import {
  OTHER_LABEL_VERTICAL_MARGIN,
  OTHER_LABEL_MAX_LINES,
  TIMELINE_LABEL_CONTAINER_PADDING_X,
  OTHER_LABEL_GAP_FACTOR,
} from '../consts';
import { text2 } from '@ardoq/typography';
import { WrapperWithEllipsisOverflow } from '@ardoq/popovers';
import { OtherLabelRenderingData, OtherLabelsRenderingData } from '../../types';
import { POPOVER_ELEMENT_TYPE } from '../../consts';

const LABEL_ROW_MARGIN_BOTTOM = 1.5;
const LABEL_ROW_BORDER_HEIGHT = 0.5;

const OtherLabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${
    OTHER_LABEL_VERTICAL_MARGIN -
    (LABEL_ROW_MARGIN_BOTTOM + LABEL_ROW_BORDER_HEIGHT)
  }px;
  padding-right: ${TIMELINE_LABEL_CONTAINER_PADDING_X}px;
  ${text2}
`;
const LabelRow = styled.div<{
  $isUnderlined: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: ${100 * OTHER_LABEL_GAP_FACTOR}%;
  ${({ $isUnderlined }) =>
    $isUnderlined &&
    css`
      margin-bottom: ${LABEL_ROW_MARGIN_BOTTOM}px;
      border-bottom: ${LABEL_ROW_BORDER_HEIGHT}px solid ${colors.blue200};
    `}
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const LabelContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const LabelText = styled.span`
  max-width: 100%;
`;

const TitleText = styled(LabelText)`
  color: ${colors.grey500};
`;

const LabelPopoverContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 448px;
  flex-direction: column;
  gap: ${space.xsmall};
`;

type LabelPopoverContent = {
  title: string;
  label: string;
};

export const OtherLabelPopoverContentRenderer = ({
  title,
  label,
}: LabelPopoverContent) => (
  <LabelPopoverContainer>
    <TitleText>{title}</TitleText>
    <span>{label}</span>
  </LabelPopoverContainer>
);

type OtherLabelProps = OtherLabelRenderingData & {
  commonTitleWidth: number;
  otherLabelIndex: number;
  isUnderlined: boolean;
  graphNodeId: string;
};
const OtherLabel = ({
  labelData,
  commonTitleWidth,
  isUnderlined,
  captionTakesFullWidth,
  graphNodeId,
  otherLabelIndex,
}: OtherLabelProps) => {
  const [title, label, showTitle] = labelData;
  return (
    <LabelRow
      $isUnderlined={isUnderlined}
      data-graph-node-id={graphNodeId}
      data-popover-element-type={POPOVER_ELEMENT_TYPE.OTHER_LABEL}
      data-label-index={otherLabelIndex}
    >
      {!captionTakesFullWidth ? (
        <TitleContainer
          style={{
            width: commonTitleWidth,
          }}
        >
          <WrapperWithEllipsisOverflow>
            <TitleText>{showTitle ? title : null}</TitleText>
          </WrapperWithEllipsisOverflow>
        </TitleContainer>
      ) : null}
      <LabelContainer>
        <WrapperWithEllipsisOverflow $lineClamp={OTHER_LABEL_MAX_LINES}>
          <LabelText>{label}</LabelText>
        </WrapperWithEllipsisOverflow>
      </LabelContainer>
    </LabelRow>
  );
};

type OtherLabelsProps = {
  graphNodeId: string;
  otherLabelsRenderingData: OtherLabelsRenderingData;
  otherLabelsContainerStyle?: React.CSSProperties;
};

export const OtherLabels = ({
  graphNodeId,
  otherLabelsRenderingData,
  otherLabelsContainerStyle,
}: OtherLabelsProps) => {
  const { labelsData: otherLabels, commonTitleWidth } =
    otherLabelsRenderingData;
  const lastLabelIndex = otherLabels.length - 1;
  return (
    <OtherLabelsContainer style={otherLabelsContainerStyle}>
      {otherLabels.map((labelRenderData, index) => {
        const isLast = index === lastLabelIndex;
        return (
          <OtherLabel
            key={`${labelRenderData.labelData[0]}-${labelRenderData.labelData[1]}-${commonTitleWidth}-${index}`}
            commonTitleWidth={commonTitleWidth}
            isUnderlined={!isLast}
            graphNodeId={graphNodeId}
            otherLabelIndex={index}
            {...labelRenderData}
          />
        );
      })}
    </OtherLabelsContainer>
  );
};
